import { useState } from 'react';
import classnames from 'classnames';
import { useQuery } from '@tanstack/react-query';

import cardStyles from '../LaboratoryService/LaboratorySelect/LaboratorySelect.module.scss';
import fhirStyles from '../LaboratoryService/FhirLaboratoryService/FhirLaboratoryService.module.scss';
import labServiceStyles from '../LaboratoryService/LaboratoryService.module.scss';

import { ReferralAttributes } from 'chaika-test-attributes';
import { PriceData } from 'chaika-frontend-types/frontend-cms';

import { SaveButton } from '@components/ActionButtons/SaveButton';
import { CancelButton } from '@components/ActionButtons/CancelButton';
import { FieldTitle } from '@components/FieldTitle';
import { translate } from '@chaika/i18n/helper';
import { legalEntityQueryOptions } from '@organization/queries/legalEntityQueries';
import { getContractorIdsFromPrices, priceQueryOptions } from '@price/queries/priceQueries';
import { Money } from '@store/models/Money';
import { store } from '@store';
import { ContractorCard } from './ContractorCard';
import { assertIsDefined } from '@utils/assertIsDefined';

interface Props {
    serviceId: string;
    contractorIds?: string[];
    redesign?: boolean;
    disabled?: boolean;
    value?: string;
    onCancel?(): void;
    onSave(contractorId: string): void;
}

export function ContractorSelect({ serviceId, contractorIds, redesign, disabled, value, onCancel, onSave }: Props) {
    const [contractorId, setContractorId] = useState<string>();

    const { legalEntityId } = store.authUser;

    const { data: prices = [] } = useQuery({
        ...priceQueryOptions.contractorPrices(serviceId, legalEntityId),
        enabled: !!legalEntityId,
    });

    const candidateIds = getCandidateIds(prices, contractorIds, value);
    const { data: contractors = [] } = useQuery(legalEntityQueryOptions.byIds(candidateIds));

    const selectedContractorId = value ?? contractorId;
    const visibleContractors = disabled ? contractors.filter(({ id }) => id === selectedContractorId) : contractors;

    function getCost(id: string): string | undefined {
        const price = prices.find(it => it.contractorId === id);

        return price ? Money.fromKopecks(price.value, price.currency).toString() : undefined;
    }

    function handleSelectClick(id: string): void {
        setContractorId(id);

        if (redesign) {
            onSave(id);
        }
    }

    function handleSaveClick(): void {
        if (contractorId) {
            onSave(contractorId);
        }
    }

    if (redesign && !value && (disabled || candidateIds.length === 0)) {
        return null;
    }

    return (
        <div
            data-testid={ReferralAttributes.ContractorSelect}
            className={classnames([redesign && labServiceStyles.root_redesign, labServiceStyles.root])}
        >
            {redesign ? <FieldTitle title={translate('dataForContractor')} /> : null}
            <div className={redesign ? labServiceStyles.field : fhirStyles.laboratory}>
                <div className={cardStyles.root}>
                    {visibleContractors.map(({ id, name }) => (
                        <ContractorCard
                            key={id}
                            name={name}
                            cost={getCost(id)}
                            disabled={disabled}
                            isSelected={id === selectedContractorId}
                            onSelectClick={() => handleSelectClick(id)}
                        />
                    ))}
                </div>
            </div>
            {redesign ? null : (
                <div className={labServiceStyles.buttons}>
                    <SaveButton
                        dataTest={ReferralAttributes.SaveContractorButton}
                        disabled={!contractorId}
                        onSaveClick={handleSaveClick}
                    />
                    <CancelButton
                        onCancelClick={() => {
                            assertIsDefined(
                                onCancel,
                                'Contractor select shows the cancel button only with a cancel handler',
                            );
                            onCancel();
                        }}
                    />
                </div>
            )}
        </div>
    );
}

function getCandidateIds(prices: PriceData[], contractorIds?: string[], value?: string): string[] {
    if (contractorIds) {
        return contractorIds;
    }

    const derivedIds = getContractorIdsFromPrices(prices);

    return !!value && !derivedIds.includes(value) ? [...derivedIds, value] : derivedIds;
}
